/**
 * Cards describing the background tool calls behind an assistant reply: schedule moves, memory
 * notes, playbook or system-prompt edits, coaching proposals awaiting review, workspace and
 * session changes, web searches with their sources, and reads. Creates and edits of activities, bundles, projects and custom fields
 * render as the form Flux Agent filled in ({@link FluxAgentFormCard}), live while the reply
 * streams. Destructive or broad actions arrive as confirmation cards with Confirm and Cancel.
 * Failed calls use a muted destructive style so the user can see what did not happen without it
 * dominating the conversation.
 *
 * @module apps/web/src/components/flux-agent/FluxAgentActionCards
 */

import { useState } from 'react';
import {
  AlertCircle,
  AudioLines,
  BatteryMedium,
  BookOpen,
  Brain,
  CalendarClock,
  Clock,
  Eye,
  FolderKanban,
  Globe,
  GitPullRequestDraft,
  Heart,
  Lightbulb,
  Loader2,
  Play,
  Plug,
  Network,
  PanelsTopLeft,
  ScrollText,
  ShieldAlert,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  fluxAgentApi,
  type FluxAgentAction,
  type FluxAgentActionKind,
  type FluxAgentLiveForm,
} from '@/lib/fluxAgentApi';
import { cn } from '@/lib/utils';
import { StructureTreeView } from '@/components/values/StructureTreeView';
import { FieldSuggestionAddButton } from './learning/FieldSuggestionAddButton';
import { FluxAgentFormCard } from './FluxAgentFormCard';
import { FluxAgentSearchSources } from './FluxAgentSearchSources';
import { FluxViewCard } from './views/FluxViewCard';
import { FluxAgentSoundPlayer } from './FluxAgentMediaViews';
import { refreshAfterAgentActions } from './plannerRefresh';

/** Lucide icon shown on each action card, chosen by the action's kind. */
const KIND_ICONS: Record<FluxAgentActionKind, LucideIcon> = {
  schedule: CalendarClock,
  memory: Brain,
  playbook: BookOpen,
  system_prompt: ScrollText,
  read: Eye,
  field_suggestion: Lightbulb,
  timing_cue: Clock,
  proposal: GitPullRequestDraft,
  workspace: FolderKanban,
  session: Play,
  confirm: ShieldAlert,
  value: Heart,
  structure: Network,
  mcp: Plug,
  search: Globe,
  trigger: Clock,
  view: PanelsTopLeft,
  sound: AudioLines,
  energy: BatteryMedium,
};

/** Inputs for {@link FluxAgentActionCards}. */
export interface FluxAgentActionCardsProps {
  /** Actions reported on one assistant message; renders nothing when empty and no live forms. */
  actions: FluxAgentAction[];
  /** Forms still being filled in while the reply streams; one whose slot has an action is replaced by it. */
  liveForms?: FluxAgentLiveForm[];
  /** Called with the outcome card after the person confirms or cancels a confirmation card. */
  onResolved?: (card: FluxAgentAction) => void;
  /** Populate the composer with a suggestion the person explicitly selected. */
  onSuggest?: (text: string) => void;
}

/** A pending confirmation card with Confirm and Cancel; shows the outcome once decided. */
function ConfirmCard({
  action,
  onResolved,
}: {
  /** The staged action awaiting the person's Confirm or Cancel. */
  action: FluxAgentAction;
  /** Receives the decided card from the server; optional when the caller does not track it. */
  onResolved?: (card: FluxAgentAction) => void;
}) {
  const [busy, setBusy] = useState<'confirm' | 'cancel' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const decide = async (decision: 'confirm' | 'cancel') => {
    if (!action.pendingId || busy) return;
    setBusy(decision);
    setError(null);
    const result =
      decision === 'confirm'
        ? await fluxAgentApi.confirmAction(action.pendingId)
        : await fluxAgentApi.cancelAction(action.pendingId);
    setBusy(null);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    onResolved?.(result.data.action);
    void refreshAfterAgentActions([result.data.action]);
  };
  return (
    <div
      className="rounded-md border border-amber-500/40 bg-amber-500/5 px-2.5 py-2 text-xs"
      data-testid="flux-agent-confirm-card"
    >
      <p className="flex items-center gap-1.5 font-medium">
        <ShieldAlert className="h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden="true" />
        {action.title}
      </p>
      <p className="text-muted-foreground mt-0.5 break-words">{action.summary}</p>
      {action.form && (
        <div className="mt-2">
          <FluxAgentFormCard form={action.form} />
        </div>
      )}
      <div className="mt-2 flex gap-2">
        <Button size="sm" className="h-7" disabled={!!busy} onClick={() => void decide('confirm')}>
          {busy === 'confirm' && (
            <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden="true" />
          )}
          Confirm
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-7"
          disabled={!!busy}
          onClick={() => void decide('cancel')}
        >
          Cancel
        </Button>
      </div>
      {error && (
        <p className="text-destructive mt-1.5" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/** List an assistant message's actions: form cards, confirmation cards, and compact cards for the rest. */
export function FluxAgentActionCards({
  actions,
  liveForms = [],
  onResolved,
  onSuggest,
}: FluxAgentActionCardsProps) {
  // A card the person resolved here shows its outcome even if the parent does not re-render it.
  const [resolved, setResolved] = useState<Record<string, FluxAgentAction>>({});
  const finished = new Set(actions.flatMap(action => (action.slot ? [action.slot] : [])));
  const filling = liveForms.filter(live => !finished.has(live.slot));
  if (!actions.length && !filling.length) return null;
  const handleResolved = (card: FluxAgentAction) => {
    if (card.pendingId) setResolved(current => ({ ...current, [card.pendingId!]: card }));
    onResolved?.(card);
  };
  return (
    <ul className="mt-2 space-y-1.5" aria-label="Actions Flux Agent took">
      {actions.map((original, index) => {
        const action = (original.pendingId && resolved[original.pendingId]) || original;
        const key = `${action.slot || action.tool}-${index}`;
        if (action.form && !(action.pendingId && action.status === 'pending'))
          return (
            <li key={key}>
              <FluxAgentFormCard form={action.form} summary={action.summary} />
            </li>
          );
        if (action.pendingId && action.status === 'pending')
          return (
            <li key={key}>
              <ConfirmCard action={action} onResolved={handleResolved} />
            </li>
          );
        if (action.kind === 'view' && action.status === 'completed' && action.viewId)
          return (
            <li key={key}>
              <FluxViewCard action={action} onSuggest={onSuggest} />
            </li>
          );
        const failed = action.status === 'failed';
        const muted = failed || action.status === 'cancelled';
        const Icon = failed ? AlertCircle : KIND_ICONS[action.kind] || Eye;
        return (
          <li
            key={key}
            className={cn(
              'flex items-start gap-2 rounded-md border px-2.5 py-2 text-xs',
              muted
                ? 'border-destructive/30 bg-destructive/5 text-muted-foreground'
                : 'border-border bg-background/70'
            )}
          >
            <Icon
              className={cn(
                'mt-0.5 h-3.5 w-3.5 shrink-0',
                muted ? 'text-destructive/80' : 'text-primary'
              )}
              aria-hidden="true"
            />
            <div className="min-w-0 flex-1">
              <p className={cn('font-medium', failed && 'text-destructive/90')}>
                {action.title}
                {failed && <span className="sr-only"> (failed)</span>}
              </p>
              {action.summary && (
                <p className="text-muted-foreground break-words">{action.summary}</p>
              )}
              {action.kind === 'field_suggestion' && <FieldSuggestionAddButton action={action} />}
              {action.kind === 'search' && action.sources && !failed && (
                <FluxAgentSearchSources sources={action.sources} />
              )}
              {action.kind === 'sound' && action.media && !failed && (
                <FluxAgentSoundPlayer media={action.media} />
              )}
              {action.kind === 'structure' && action.structure && !failed && (
                <div className="mt-2">
                  <StructureTreeView
                    tree={action.structure.tree}
                    findings={action.structure.findings}
                    compact
                  />
                </div>
              )}
            </div>
          </li>
        );
      })}
      {filling.map(live => (
        <li key={`live-${live.slot}`}>
          <FluxAgentFormCard form={live.form} />
        </li>
      ))}
    </ul>
  );
}
